import { FormDecoration } from "@general-backoffice/core/index";
import type { TaxProfile } from "@investhinks/api-client";
import React, { useState } from "react";
import { Alert, Button, Card, CardBody, Spinner } from "reactstrap";

import { validateEUVAT } from "../validators";

type VIESStepTaxProfile = Pick<TaxProfile, "taxIdNumber" | "viesVerified" | "viesName" | "viesAddress"> & Record<string, unknown>;

interface VIESStepProps {
  taxProfile: VIESStepTaxProfile | null;
  onVerifyVIES: (vatNumber: string) => Promise<void>;
  isLoading: boolean;
  t: (key: string, fallback?: string) => string;
}

const VIESStep = ({ taxProfile, onVerifyVIES, isLoading, t }: VIESStepProps) => {
  const [isVerifying, setIsVerifying] = useState(false);

  const vatNumber = taxProfile?.taxIdNumber || "";
  const hasValidCountryCode = vatNumber ? validateEUVAT(vatNumber).valid : false;

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await onVerifyVIES(taxProfile?.taxIdNumber || "");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div>
      <h5 className="mb-3">{t("steps.vies.title", "VIES Verification")}</h5>
      <p className="text-muted mb-4">
        {t("steps.vies.description", "Your EU VAT number must be verified through the VIES system.")}
      </p>

      <Card className="bg-light">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>{t("steps.vies.vatNumber", "VAT Number")}:</strong>
              <br />
              <span className="h4">{vatNumber || "—"}</span>
            </div>
            <div className="text-right">
              {taxProfile?.viesVerified ? (
                <span className="text-success">
                  <i className="fas fa-check-circle fa-2x" />
                  <br />
                  {t("steps.vies.verified", "Verified")}
                </span>
              ) : (
                <span className="text-muted">
                  <i className="fas fa-times-circle fa-2x" />
                  <br />
                  {t("steps.vies.notVerified", "Not Verified")}
                </span>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Warning if VAT number doesn't start with EU country code */}
      {vatNumber && !hasValidCountryCode && !taxProfile?.viesVerified && (
        <Alert color="warning" className="mt-3">
          <h5 className="alert-heading">
            <i className="fas fa-exclamation-triangle mr-2" />
            {t("steps.vies.invalidFormat", "Invalid VAT Number Format")}
          </h5>
          <p>
            {t(
              "steps.vies.countryCodeRequired",
              "Your VAT number must start with a 2-letter EU country code (e.g., DE for Germany, FR for France, BG for Bulgaria)."
            )}
          </p>
          <hr />
          <p className="mb-0">
            <strong>{t("steps.vies.whatIsVIES", "What is VIES?")}</strong>{" "}
            {t(
              "steps.vies.viesExplanation",
              "VIES (VAT Information Exchange System) is the European Commission's database for validating EU VAT numbers. To receive tax-exempt payments as an EU business, your company must be registered for intra-community VAT and have a valid VAT number that can be verified through VIES."
            )}
          </p>
          <p className="mt-2 mb-0">
            <strong>{t("steps.vies.example", "Example")}:</strong>{" "}
            {t(
              "steps.vies.formatExample",
              "If your company is in Bulgaria with VAT number 207883265, enter it as BG207883265."
            )}
          </p>
        </Alert>
      )}

      {!taxProfile?.viesVerified && (
        <Button
          color="primary"
          onClick={handleVerify}
          disabled={isLoading || isVerifying || !vatNumber || !hasValidCountryCode}
          block
          className="mt-3"
        >
          {isVerifying ? (
            <>
              <Spinner size="sm" className="mr-2" />
              {t("steps.vies.verifying", "Verifying...")}
            </>
          ) : (
            <>
              <i className="fas fa-check-circle mr-2" />
              {t("steps.vies.verifyButton", "Verify VAT Number")}
            </>
          )}
        </Button>
      )}

      {taxProfile?.viesName && (
        <div className="mt-3">
          <FormDecoration.LineTitle title={t("steps.vies.result", "VIES Result")} />
          <p>
            <strong>{t("steps.vies.name", "Name")}:</strong> {taxProfile.viesName}
          </p>
          {taxProfile?.viesAddress && (
            <p>
              <strong>{t("steps.vies.address", "Address")}:</strong> {taxProfile.viesAddress}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VIESStep;
